/**
 * TOSS Web Application - Notifications TanStack Query Hooks
 *
 * React hooks for notifications using TanStack Query:
 * - useNotifications - Get paginated notifications list
 * - useUnreadCount - Get unread notifications count
 * - useMarkAsRead - Mark single notification as read
 * - useMarkAllAsRead - Mark all notifications as read
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { queryKeys } from '@/app/queryClient';
import { useAuthStore } from '@/store/authStore';
import {
  notificationsApi,
  type NotificationListRequest,
  type NotificationListResponse,
} from '@/api/notifications.api';
import type { Notification, SuccessResponse, UnreadNotificationsCount } from '@/types';
import { pagedInfiniteOptions } from './pagination';

// ============================================================================
// Types
// ============================================================================

/**
 * Options for useNotifications hook
 */
export interface UseNotificationsOptions {
  /** Whether the query is enabled */
  enabled?: boolean;
  /** Polling interval in ms (0 to disable) */
  refetchInterval?: number;
}

/**
 * Options for useUnreadCount hook
 */
export interface UseUnreadCountOptions {
  /** Whether the query is enabled */
  enabled?: boolean;
  /** Polling interval in ms for badge updates */
  refetchInterval?: number;
}

/**
 * Options for useMarkAsRead hook
 */
export interface UseMarkAsReadOptions {
  /** Callback on successful mark as read */
  onSuccess?: (data: SuccessResponse, notificationId: string) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

/**
 * Options for useMarkAllAsRead hook
 */
export interface UseMarkAllAsReadOptions {
  /** Callback on successful mark all as read */
  onSuccess?: (data: SuccessResponse) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

// ============================================================================
// Cache shapes
// ============================================================================

/**
 * What sits under `queryKeys.notifications.lists()`. The bell panel keeps one page
 * (`useNotifications`), the full page keeps an infinite query (`useNotificationsInfinite`), and
 * both share the key prefix — so every updater has to understand both, or the first mark-read
 * after the full page was opened throws inside `onSuccess` on `oldData.items.map`.
 */
type CachedNotificationList = NotificationListResponse | InfiniteData<NotificationListResponse>;

function isInfinite(data: CachedNotificationList): data is InfiniteData<NotificationListResponse> {
  return 'pages' in data;
}

/** Applies `update` to the rows of one cached list, whichever of the two shapes it has. */
function mapCachedItems(
  data: CachedNotificationList | undefined,
  update: (items: Notification[]) => Notification[]
): CachedNotificationList | undefined {
  if (!data) return data;
  if (isInfinite(data)) {
    return { ...data, pages: data.pages.map((page) => ({ ...page, items: update(page.items) })) };
  }
  return { ...data, items: update(data.items) };
}

/** Finds a row in any cached list — to learn whether the row being read was a priority one. */
function findCachedNotification(queryClient: QueryClient, id: string): Notification | undefined {
  for (const [, data] of queryClient.getQueriesData<CachedNotificationList>({
    queryKey: queryKeys.notifications.lists(),
  })) {
    if (!data) continue;
    const pages = isInfinite(data) ? data.pages : [data];
    for (const page of pages) {
      const found = page.items.find((n) => n.id === id);
      if (found) return found;
    }
  }
  return undefined;
}

// ============================================================================
// Notifications List Hook
// ============================================================================

/**
 * Hook for fetching paginated notifications list
 *
 * @param filters - Filter and pagination parameters
 * @param options - Query options
 * @returns Query for notifications list
 *
 * @example
 * ```tsx
 * function NotificationsList() {
 *   const { data, isLoading, error, refetch } = useNotifications({
 *     page: 1,
 *     pageSize: 20,
 *     isRead: false
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <ul>
 *       {data?.items.map(notification => (
 *         <NotificationItem key={notification.id} notification={notification} />
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useNotifications(
  filters?: NotificationListRequest,
  options?: UseNotificationsOptions
) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<NotificationListResponse, Error>({
    queryKey: queryKeys.notifications.list(filters),
    queryFn: () => notificationsApi.getNotifications(filters),
    enabled: options?.enabled ?? isAuthenticated,
    staleTime: 1 * 60 * 1000, // 1 minute - notifications should be relatively fresh
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: options?.refetchInterval,
    placeholderData: (previousData) => previousData,
  });
}

// ============================================================================
// Notifications Infinite Hook
// ============================================================================

/**
 * Hook for infinite scrolling notifications list (used by DataTable migration)
 */
export function useNotificationsInfinite(
  filter?: Omit<NotificationListRequest, 'page'>,
  options?: UseNotificationsOptions
) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useInfiniteQuery<NotificationListResponse, Error>({
    queryKey: queryKeys.notifications.list(filter),
    queryFn: ({ pageParam = 1 }) =>
      notificationsApi.getNotifications({ ...filter, page: pageParam as number }),
    ...pagedInfiniteOptions,
    enabled: options?.enabled ?? isAuthenticated,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

// ============================================================================
// Unread Count Hook
// ============================================================================

/**
 * Hook for fetching unread notifications count
 *
 * @param options - Query options
 * @returns Query for unread count
 *
 * @example
 * ```tsx
 * function NotificationBadge() {
 *   const { data } = useUnreadCount({
 *     refetchInterval: 30000 // Refresh every 30 seconds
 *   });
 *
 *   if (!data?.count) return null;
 *
 *   return <Badge variant="error">{data.count}</Badge>;
 * }
 * ```
 */
export function useUnreadCount(options?: UseUnreadCountOptions) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<UnreadNotificationsCount, Error>({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => notificationsApi.getUnreadCount(),
    enabled: options?.enabled ?? isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds - count should be fresh
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: options?.refetchInterval ?? 30000, // Default 30 second polling
    refetchIntervalInBackground: false, // Don't poll when tab is not active
  });
}

// ============================================================================
// Mark As Read Hook
// ============================================================================

/**
 * Hook for marking a single notification as read
 *
 * @param options - Mutation options
 * @returns Mutation for marking as read
 *
 * @example
 * ```tsx
 * function NotificationItem({ notification }) {
 *   const markAsRead = useMarkAsRead({
 *     onSuccess: () => toast.success('Marked as read')
 *   });
 *
 *   return (
 *     <div
 *       onClick={() => {
 *         if (!notification.isRead) {
 *           markAsRead.mutate(notification.id);
 *         }
 *       }}
 *     >
 *       {notification.title}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMarkAsRead(options?: UseMarkAsReadOptions) {
  const queryClient = useQueryClient();

  return useMutation<SuccessResponse, Error, string>({
    mutationFn: notificationsApi.markAsRead,
    onSuccess: (data, notificationId) => {
      // Read before the rows are rewritten: whether the red count moves depends on the row.
      const row = findCachedNotification(queryClient, notificationId);

      // Optimistically update the notification in the cache
      queryClient.setQueriesData<CachedNotificationList>(
        { queryKey: queryKeys.notifications.lists() },
        (oldData) =>
          mapCachedItems(oldData, (items) =>
            items.map((notification) =>
              notification.id === notificationId
                ? { ...notification, isRead: true }
                : notification
            )
          )
      );

      if (!row) {
        // A row no cached list holds — the counts cannot be adjusted blind, ask the server.
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
      } else if (!row.isRead) {
        queryClient.setQueryData<UnreadNotificationsCount>(
          queryKeys.notifications.unreadCount(),
          (oldData) => {
            if (!oldData) return oldData;
            return {
              count: Math.max(0, oldData.count - 1),
              priorityCount: row.isPriority
                ? Math.max(0, (oldData.priorityCount ?? 0) - 1)
                : oldData.priorityCount ?? 0,
            };
          }
        );
      }

      // Call user's callback
      options?.onSuccess?.(data, notificationId);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

// ============================================================================
// Mark All As Read Hook
// ============================================================================

/**
 * Hook for marking all notifications as read
 *
 * @param options - Mutation options
 * @returns Mutation for marking all as read
 *
 * @example
 * ```tsx
 * function NotificationsHeader() {
 *   const markAllAsRead = useMarkAllAsRead({
 *     onSuccess: () => toast.success('All notifications marked as read')
 *   });
 *
 *   return (
 *     <button onClick={() => markAllAsRead.mutate()}>
 *       Mark All as Read
 *     </button>
 *   );
 * }
 * ```
 */
export function useMarkAllAsRead(options?: UseMarkAllAsReadOptions) {
  const queryClient = useQueryClient();

  return useMutation<SuccessResponse, Error, void>({
    mutationFn: notificationsApi.markAllAsRead,
    onSuccess: (data) => {
      // Mark all notifications in cache as read
      queryClient.setQueriesData<CachedNotificationList>(
        { queryKey: queryKeys.notifications.lists() },
        (oldData) =>
          mapCachedItems(oldData, (items) =>
            items.map((notification) => ({ ...notification, isRead: true }))
          )
      );

      // Set both unread counts to 0
      queryClient.setQueryData<UnreadNotificationsCount>(
        queryKeys.notifications.unreadCount(),
        { count: 0, priorityCount: 0 }
      );

      // A filtered list («Непрочитанные») now holds rows that no longer match it.
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });

      // Call user's callback
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

// ============================================================================
// Helper Functions for Real-time Updates
// ============================================================================

/**
 * Add a new notification to the cache (for SignalR real-time updates)
 *
 * @param queryClient - TanStack Query client
 * @param notification - New notification to add
 */
export function addNotificationToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  notification: Notification
): void {
  // Refetch rather than prepend: the cached lists are filtered now (the «Важные» and
  // «Непрочитанные» chips, the page's column filters), and a row prepended into a list it does
  // not match is worse than a list that is one round trip late. Only mounted lists refetch.
  queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });

  // The counts move at once — the red one is what the push is for.
  if (!notification.isRead) {
    queryClient.setQueryData<UnreadNotificationsCount>(
      queryKeys.notifications.unreadCount(),
      (oldData) => ({
        count: (oldData?.count ?? 0) + 1,
        priorityCount: (oldData?.priorityCount ?? 0) + (notification.isPriority ? 1 : 0),
      })
    );
  }
}

/**
 * Invalidate all notification queries (for SignalR reconnection)
 *
 * @param queryClient - TanStack Query client
 */
export function invalidateNotifications(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.notifications.all,
  });
}

// ============================================================================
// Export
// ============================================================================

export const notificationsHooks = {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  addNotificationToCache,
  invalidateNotifications,
} as const;

export default notificationsHooks;
